import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useField } from 'formik';
import { Box, FormControl, Input, FormHelperText, InputAdornment } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { isEmpty, find } from 'lodash';

import { ContentSharePreviewFragment } from 'api';
import { useProtectedClient, useContent } from 'hooks';

import { INetwork, IShareNetworkAuth } from '../types';
import Count from './Count';
import Note from 'components/Note';

interface IProps {
  network: INetwork;
  previewInfo?: ContentSharePreviewFragment;
}

const useStyles = makeStyles(theme => ({
  inputRoot: {
    backgroundColor: 'transparent',
    border: `1px solid ${theme.palette.grey[300]}`,
  },
}));

const Commentary: FC<IProps> = ({ network, previewInfo }) => {
  const classes = useStyles();
  const content = useContent();
  const client = useProtectedClient();
  const { t } = useTranslation();

  const { provider } = network;
  const [commentaryField, form] = useField<IShareNetworkAuth>(`${provider}.user_commentary`);

  const { error, touched } = form;

  const fieldValue = commentaryField.value || '';
  const hasError = !!error && touched;
  const lockedCommentary = (!isEmpty(previewInfo) && previewInfo.locked_commentary) || false;
  const placeholder = !lockedCommentary ? `sharing:${provider}` : '';
  const disclosure = getContentDisclosure();

  function getContentDisclosure() {
    const contentDisclosure = find(content?.disclosures, ['network', provider]);
    if (!contentDisclosure) {
      return null;
    }

    const clientDisclosure = find(client.disclosures, ['id', contentDisclosure.disclosure_id]);
    if (!clientDisclosure) {
      return null;
    }

    return clientDisclosure.disclosure;
  }

  return (
    <Box mt={0.5} width="100%">
      <FormControl margin="none" fullWidth error={hasError}>
        <Input
          multiline
          disableUnderline
          value={fieldValue}
          placeholder={t(placeholder)}
          onChange={commentaryField.onChange}
          inputProps={{ name: commentaryField.name }}
          disabled={lockedCommentary}
          className={classes.inputRoot}
          endAdornment={
            <InputAdornment position="end">
              <Count network={network} />
            </InputAdornment>
          }
        />
        {hasError && <FormHelperText>{error}</FormHelperText>}
      </FormControl>
      {!!disclosure && <Note color="inherit">{t('sharing:disclosureTip', { disclosure })}</Note>}
      {lockedCommentary && <Note i18nKey="sharing:shareLockdownTip" />}
    </Box>
  );
};

Commentary.defaultProps = {
  previewInfo: {} as ContentSharePreviewFragment,
};

export default Commentary;
